/**
 * Spot Studio shared data hooks.
 *
 * The hooks here are read-only (RLS-protected) plus thin wrappers around
 * existing Edge Functions / RPC helpers. No business logic lives in the
 * frontend — geocoding stays in `resolve_geo_context`, contributions go
 * through `spot-contribution-submit`, etc.
 */
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "./useAuth";
import { useUserRoles, type AppRole } from "./useUserRoles";

const STUDIO_ROLES: AppRole[] = [
  "admin",
  "super_admin",
  "spot_admin",
  "verifier",
  "moderator",
  "local_guide",
  "partner",
];

export function useSpotStudioAccess() {
  const { roles, loading, isAuthenticated } = useUserRoles();
  const canAccess = roles.some((r) => STUDIO_ROLES.includes(r as AppRole));
  const canReview = roles.some((r) =>
    ["admin", "super_admin", "spot_admin", "verifier", "moderator"].includes(r),
  );
  return { canAccess, canReview, roles, loading, isAuthenticated };
}

// ─── Tasks (spot_enrichment_tasks) ─────────────────────────────
export interface StudioTask {
  id: string;
  spot_id: string;
  workflow_status: string;
  assignee_id: string | null;
  notes: string | null;
  in_pilot: boolean;
  last_status_change_at: string;
  spot: {
    id: string;
    slug: string | null;
    name_json: Record<string, string> | null;
    geo_lat: number | null;
    geo_lng: number | null;
    place_id: string | null;
  } | null;
}

export function useStudioTasks(filter: "open" | "in_review" | "geo_review" | "verification" | "mine" = "open") {
  const { user } = useAuth();
  return useQuery({
    queryKey: ["spot-studio-tasks", filter, user?.id ?? null],
    queryFn: async (): Promise<StudioTask[]> => {
      let q = supabase
        .from("spot_enrichment_tasks")
        .select(
          "id, spot_id, workflow_status, assignee_id, notes, in_pilot, last_status_change_at, spot:spots(id, slug, name_json, geo_lat, geo_lng, place_id)",
        )
        .order("last_status_change_at", { ascending: false })
        .limit(100);

      if (filter === "open") q = q.in("workflow_status", ["open", "todo", "new"]);
      if (filter === "in_review") q = q.in("workflow_status", ["in_review", "review"]);
      if (filter === "geo_review") q = q.eq("workflow_status", "geo_review");
      if (filter === "verification") q = q.in("workflow_status", ["verification", "verify"]);
      if (filter === "mine" && user) q = q.eq("assignee_id", user.id);

      const { data, error } = await q;
      if (error) throw error;
      return (data ?? []) as unknown as StudioTask[];
    },
    staleTime: 30_000,
  });
}

export function useStudioTask(taskId: string | undefined) {
  return useQuery({
    queryKey: ["spot-studio-task", taskId],
    queryFn: async (): Promise<StudioTask | null> => {
      if (!taskId) return null;
      const { data, error } = await supabase
        .from("spot_enrichment_tasks")
        .select(
          "id, spot_id, workflow_status, assignee_id, notes, in_pilot, last_status_change_at, spot:spots(id, slug, name_json, geo_lat, geo_lng, place_id)",
        )
        .eq("id", taskId)
        .maybeSingle();
      if (error) throw error;
      return (data ?? null) as unknown as StudioTask | null;
    },
    enabled: !!taskId,
  });
}

// ─── Geo candidates ────────────────────────────────────────────
export interface GeoCandidate {
  id: string;
  confidence: number;
  tier: "auto" | "review" | "uncertain";
  status: string;
  proposed_country_code: string | null;
  proposed_region_name: string | null;
  proposed_place_name: string | null;
  country_id: string | null;
  region_id: string | null;
  place_id: string | null;
  latitude: number | null;
  longitude: number | null;
  created_at: string;
}

export function useGeoCandidates(spotId: string | undefined) {
  return useQuery({
    queryKey: ["spot-geo-candidates", spotId],
    queryFn: async (): Promise<GeoCandidate[]> => {
      if (!spotId) return [];
      const { data, error } = await supabase
        .from("spot_geo_resolution_candidates")
        .select(
          "id, confidence, tier, status, proposed_country_code, proposed_region_name, proposed_place_name, country_id, region_id, place_id, latitude, longitude, created_at",
        )
        .eq("spot_id", spotId)
        .order("created_at", { ascending: false })
        .limit(10);
      if (error) throw error;
      return (data ?? []) as GeoCandidate[];
    },
    enabled: !!spotId,
  });
}

/** Triggers a new geo lookup via the SECURITY DEFINER RPC. */
export async function enqueueGeoResolve(spotId: string): Promise<string | null> {
  const { data, error } = await supabase.rpc("enqueue_resolve_geo_context", {
    _spot_id: spotId,
  });
  if (error) throw error;
  return (data as string | null) ?? null;
}

// ─── Contributions (mine) ──────────────────────────────────────
export interface StudioContribution {
  id: string;
  spot_id: string | null;
  contribution_type: string;
  state: string;
  payload_json: Record<string, unknown> | null;
  rejection_reason: string | null;
  reviewed_at: string | null;
  created_at: string;
  spot: { id: string; slug: string | null; name_json: Record<string, string> | null } | null;
}

export function useMyContributions() {
  const { user } = useAuth();
  return useQuery({
    queryKey: ["spot-studio-my-contributions", user?.id ?? null],
    queryFn: async (): Promise<StudioContribution[]> => {
      if (!user) return [];
      const { data, error } = await supabase
        .from("spot_contributions")
        .select(
          "id, spot_id, contribution_type, state, payload_json, rejection_reason, reviewed_at, created_at, spot:spots(id, slug, name_json)",
        )
        .eq("submitted_by", user.id)
        .order("created_at", { ascending: false })
        .limit(50);
      if (error) throw error;
      return (data ?? []) as unknown as StudioContribution[];
    },
    enabled: !!user,
  });
}

/** Open contributions awaiting review (excluding the current user's own). */
export function useReviewQueue() {
  const { user } = useAuth();
  return useQuery({
    queryKey: ["spot-studio-review-queue", user?.id ?? null],
    queryFn: async (): Promise<StudioContribution[]> => {
      // Reviewer-gated SECURITY DEFINER RPC (excludes own submissions
      // and enforces the reviewer-role check server-side).
      const { data, error } = await supabase.rpc("studio_review_queue", {
        _limit: 50,
      });
      if (error) throw error;
      return ((data ?? []) as Array<{
        id: string;
        spot_id: string;
        contribution_type: string;
        state: string;
        payload_json: Record<string, unknown> | null;
        submitted_by: string;
        created_at: string;
        spot_slug: string | null;
        spot_name_json: Record<string, string> | null;
      }>).map((r) => ({
        id: r.id,
        spot_id: r.spot_id,
        contribution_type: r.contribution_type,
        state: r.state,
        payload_json: r.payload_json,
        rejection_reason: null,
        reviewed_at: null,
        created_at: r.created_at,
        spot: { id: r.spot_id, slug: r.spot_slug, name_json: r.spot_name_json },
      }));
    },
    enabled: !!user,
  });
}

// ─── Review action ─────────────────────────────────────────────
export type ReviewDecision = "accept" | "reject" | "keep_open";

export async function reviewContribution(
  contributionId: string,
  decision: ReviewDecision,
  reviewNote = "",
): Promise<{ ok: true; state: string }> {
  const { data, error } = await supabase.functions.invoke(
    "spot-contribution-review",
    { body: { contribution_id: contributionId, decision, review_note: reviewNote } },
  );
  if (error) throw error;
  if (!data?.ok) throw new Error(data?.error ?? "review_failed");
  return data as { ok: true; state: string };
}

// ─── Contribution submission ───────────────────────────────────
export type ContributionType =
  | "add_entry" | "add_parking" | "add_route"
  | "add_teaser" | "add_note" | "add_hazard"
  | "verify_info" | "add_spot_point" | "add_media";

export interface SubmitContributionInput {
  spot_id: string;
  contribution_type: ContributionType;
  /** Free-text body (5–4000 chars). For structured forms, build it client-side. */
  content: string;
  meta?: {
    title?: string;
    language?: string;
    latitude?: number;
    longitude?: number;
    severity?: string;
    checks?: Record<string, boolean>;
    note?: string;
  };
}

export async function submitContribution(
  input: SubmitContributionInput,
): Promise<{ id: string; state: string }> {
  const { data, error } = await supabase.functions.invoke("spot-contribution-submit", {
    body: {
      spot_id: input.spot_id,
      contribution_type: input.contribution_type,
      payload: { content: input.content, meta: input.meta ?? undefined },
    },
  });
  if (error) throw error;
  if (!data?.id) throw new Error(data?.error ?? "submit_failed");
  return data as { id: string; state: string };
}

// ─── Claim (Phase 2) ──────────────────────────────────────────
export type ClaimIntent =
  | "enrichment"
  | "verification"
  | "geo_review"
  | "safety_review"
  | "description_review";

export interface ClaimResult {
  ok: true;
  task_id: string;
  status: "claimed" | "created_and_claimed" | "already_owned";
  navigate_to: string;
}

export class ClaimError extends Error {
  constructor(public code: string, public status: number) {
    super(code);
    this.name = "ClaimError";
  }
}

// ─── Discovery (Phase 3) ──────────────────────────────────────
export type DiscoverFilter =
  | "open"
  | "needs_enrichment"
  | "ready_for_verification"
  | "geo_review"
  | "recent";

export interface DiscoverSpot {
  spot_id: string;
  slug: string | null;
  name_json: Record<string, string> | null;
  country_code: string | null;
  region_code: string | null;
  waterbody_id: string | null;
  geo_lat: number | null;
  geo_lng: number | null;
  quality_status: import("@/lib/spotDisplayStatus").QualityStatus | null;
  enrichment_level: string;
  enrichment_blockers: unknown;
  has_geo: boolean;
  has_description: boolean;
  has_entry: boolean;
  has_safety: boolean;
  has_recent_visibility: boolean;
  has_media: boolean;
  has_spot_point: boolean;
  has_default_variant: boolean;
  last_activity_at: string | null;
  task_id: string | null;
  task_status: string | null;
  task_assignee_id: string | null;
  task_in_pilot: boolean | null;
}

export interface DiscoverOptions {
  search?: string;
  country?: string;
  waterbody?: string;
  limit?: number;
  offset?: number;
}

export interface DiscoverResult {
  spots: DiscoverSpot[];
  total: number;
}

export function useDiscoverSpots(
  filter: DiscoverFilter = "open",
  options: DiscoverOptions = {},
) {
  const { canAccess } = useSpotStudioAccess();
  const { search = "", country, waterbody, limit = 30, offset = 0 } = options;
  const trimmed = search.trim();
  return useQuery({
    queryKey: [
      "spot-studio-discover",
      filter,
      trimmed.toLowerCase(),
      country ?? null,
      waterbody ?? null,
      limit,
      offset,
    ],
    queryFn: async (): Promise<DiscoverResult> => {
      const { data, error } = await supabase.rpc("studio_discover_spots", {
        _filter: filter,
        _country: country ?? undefined,
        _waterbody: waterbody ?? undefined,
        _search: trimmed.length > 0 ? trimmed : undefined,
        _limit: limit,
        _offset: offset,
      });
      if (error) throw error;
      const rows = (data ?? []) as Array<DiscoverSpot & { total_count?: number | null }>;
      const total = rows[0]?.total_count != null ? Number(rows[0].total_count) : rows.length;
      return { spots: rows.map(({ total_count: _t, ...r }) => r), total };
    },
    enabled: canAccess,
    staleTime: 15_000,
  });
}

/**
 * Best-fit intent suggestion based on the discovery row.
 * UX hint only — backend remains authoritative and may reject.
 */
export function suggestClaimIntent(s: DiscoverSpot): ClaimIntent {
  if (s.enrichment_level === "full" && s.quality_status !== "verified") {
    return "verification";
  }
  if (!s.has_geo || !s.has_spot_point) return "geo_review";
  if (!s.has_description) return "description_review";
  if (!s.has_safety) return "safety_review";
  return "enrichment";
}

/**
 * Claim an open spot for a given intent. Curated studio roles only —
 * the edge function and the underlying RPC enforce this server-side.
 * No direct DB writes; all state changes happen in `studio_claim_spot`.
 */
export async function claimSpot(
  spotId: string,
  intent: ClaimIntent,
): Promise<ClaimResult> {
  const { data, error } = await supabase.functions.invoke("spot-studio-claim", {
    body: { spot_id: spotId, intent },
  });
  if (error) {
    // FunctionsHttpError carries the upstream JSON body in `context`.
    const ctx = (error as { context?: { status?: number; body?: unknown } }).context;
    let code = "claim_failed";
    const status = ctx?.status ?? 500;
    if (ctx?.body && typeof ctx.body === "object" && "error" in ctx.body) {
      code = String((ctx.body as { error: unknown }).error);
    }
    throw new ClaimError(code, status);
  }
  if (!data?.ok || !data.task_id) {
    throw new ClaimError(data?.error ?? "claim_failed", 500);
  }
  return data as ClaimResult;
}

// ─── Rewards / level ───────────────────────────────────────────
export interface RewardBalance {
  total_points: number;
  last_reward_at: string | null;
}

export function useRewardBalance() {
  const { user } = useAuth();
  return useQuery({
    queryKey: ["spot-studio-reward-balance", user?.id ?? null],
    queryFn: async (): Promise<RewardBalance> => {
      if (!user) return { total_points: 0, last_reward_at: null };
      const { data, error } = await supabase
        .from("user_reward_balance")
        .select("total_points, last_reward_at")
        .eq("user_id", user.id)
        .maybeSingle();
      if (error) throw error;
      return data ?? { total_points: 0, last_reward_at: null };
    },
    enabled: !!user,
  });
}

export interface ContributorLevel {
  level: number;
  code: string;
  name_de: string;
  name_en: string;
  min_points: number;
  next_level: number | null;
  next_min_points: number | null;
  points_to_next: number;
}

export function useContributorLevel(points: number | undefined) {
  return useQuery({
    queryKey: ["contributor-level", points ?? 0],
    queryFn: async (): Promise<ContributorLevel | null> => {
      const { data, error } = await supabase.rpc("compute_contributor_level", {
        _points: points ?? 0,
      });
      if (error) throw error;
      const row = Array.isArray(data) ? data[0] : data;
      return (row as ContributorLevel) ?? null;
    },
    enabled: points !== undefined,
  });
}

export function useRecentRewards(limit = 10) {
  const { user } = useAuth();
  return useQuery({
    queryKey: ["spot-studio-recent-rewards", user?.id ?? null, limit],
    queryFn: async () => {
      if (!user) return [];
      const { data, error } = await supabase
        .from("reward_ledger")
        .select("id, points, source_event_type, reason, created_at")
        .eq("user_id", user.id)
        .order("created_at", { ascending: false })
        .limit(limit);
      if (error) throw error;
      return data ?? [];
    },
    enabled: !!user,
  });
}

export function useMyBadges() {
  const { user } = useAuth();
  return useQuery({
    queryKey: ["spot-studio-badges", user?.id ?? null],
    queryFn: async () => {
      if (!user) return [];
      const { data, error } = await supabase
        .from("user_badges")
        .select("id, awarded_at, badge:badge_definitions(code, name, description, icon_key, tier)")
        .eq("user_id", user.id)
        .order("awarded_at", { ascending: false });
      if (error) throw error;
      return data ?? [];
    },
    enabled: !!user,
  });
}

// ─── Helpers ───────────────────────────────────────────────────
export function pickName(
  json: Record<string, string> | null | undefined,
  locale: string,
  fallback = "Spot",
): string {
  if (!json) return fallback;
  return (
    json[locale] ||
    json.de ||
    json.en ||
    Object.values(json).find((v) => typeof v === "string" && v.trim()) ||
    fallback
  );
}

export function tierLabelKey(tier: GeoCandidate["tier"]): string {
  if (tier === "auto") return "spotStudio.geo.tier.auto";
  if (tier === "review") return "spotStudio.geo.tier.review";
  return "spotStudio.geo.tier.uncertain";
}

export function contributionStateLabelKey(state: string): string {
  return `spotStudio.contributions.state.${state}`;
}

// ─── Existing-data hooks (read-only, RLS-protected) ────────────
// Used by the wizard steps to surface what's already on the spot
// before asking the user to contribute more.

export interface SpotDetails {
  short_teaser_json: Record<string, string> | null;
  description_json: Record<string, string> | null;
}

export function useSpotDetails(spotId: string | undefined) {
  return useQuery({
    queryKey: ["spot-studio-spot-details", spotId],
    queryFn: async (): Promise<SpotDetails | null> => {
      if (!spotId) return null;
      const { data, error } = await supabase
        .from("spots")
        .select("short_teaser_json, description_json")
        .eq("id", spotId)
        .maybeSingle();
      if (error) throw error;
      return (data ?? null) as SpotDetails | null;
    },
    enabled: !!spotId,
    staleTime: 30_000,
  });
}

export interface SpotPlaceContext {
  place: { id: string; place_name: string; display_name_json: Record<string, string> | null } | null;
  region: { id: string; code: string; name_de: string | null; name_en: string | null } | null;
  country: { id: string; iso2: string; name_de: string | null; name_en: string | null } | null;
}

export function useSpotPlaceContext(placeId: string | null | undefined) {
  return useQuery({
    queryKey: ["spot-studio-place-context", placeId],
    queryFn: async (): Promise<SpotPlaceContext | null> => {
      if (!placeId) return null;
      const { data, error } = await supabase
        .from("places")
        .select(
          "id, place_name, display_name_json, regions(id, code, name_de, name_en), countries(id, iso2, name_de, name_en)",
        )
        .eq("id", placeId)
        .maybeSingle();
      if (error) throw error;
      if (!data) return null;
      const row = data as unknown as {
        id: string;
        place_name: string;
        display_name_json: Record<string, string> | null;
        regions: SpotPlaceContext["region"] | null;
        countries: SpotPlaceContext["country"] | null;
      };
      return {
        place: { id: row.id, place_name: row.place_name, display_name_json: row.display_name_json },
        region: row.regions ?? null,
        country: row.countries ?? null,
      };
    },
    enabled: !!placeId,
    staleTime: 60_000,
  });
}

export interface SpotAccessPointSummary {
  id: string;
  type: string | null;
  is_primary: boolean;
  name_json: Record<string, string> | null;
  point_description_json: Record<string, string> | null;
  coordinates_json: { lat?: number; lng?: number } | null;
}

export function useSpotAccessPoints(spotId: string | undefined) {
  return useQuery({
    queryKey: ["spot-studio-access-points", spotId],
    queryFn: async (): Promise<SpotAccessPointSummary[]> => {
      if (!spotId) return [];
      const { data, error } = await supabase
        .from("access_points")
        .select("id, type, is_primary, name_json, point_description_json, coordinates_json")
        .eq("spot_id", spotId)
        .order("is_primary", { ascending: false })
        .order("sort_order", { ascending: true });
      if (error) throw error;
      return (data ?? []) as unknown as SpotAccessPointSummary[];
    },
    enabled: !!spotId,
    staleTime: 30_000,
  });
}

export interface SpotHazardSummary {
  id: string;
  severity_override: number | null;
  note: string | null;
  hazard_definitions: {
    code: string;
    category: string;
    label_json: Record<string, string> | null;
    severity_default: number | null;
  } | null;
}

export function useSpotHazardsList(spotId: string | undefined) {
  return useQuery({
    queryKey: ["spot-studio-hazards", spotId],
    queryFn: async (): Promise<SpotHazardSummary[]> => {
      if (!spotId) return [];
      const { data, error } = await supabase
        .from("spot_hazards")
        .select(
          "id, severity_override, note, hazard_definitions(code, category, label_json, severity_default)",
        )
        .eq("spot_id", spotId)
        .order("created_at", { ascending: true });
      if (error) throw error;
      return (data ?? []) as unknown as SpotHazardSummary[];
    },
    enabled: !!spotId,
    staleTime: 30_000,
  });
}

/** All contributions for a spot, scoped by RLS to what the current user may see. */
export function useSpotContributionsForSpot(spotId: string | undefined) {
  return useQuery({
    queryKey: ["spot-studio-spot-contributions", spotId],
    queryFn: async (): Promise<StudioContribution[]> => {
      if (!spotId) return [];
      const { data, error } = await supabase
        .from("spot_contributions")
        .select(
          "id, spot_id, contribution_type, state, payload_json, rejection_reason, reviewed_at, created_at",
        )
        .eq("spot_id", spotId)
        .order("created_at", { ascending: false })
        .limit(50);
      if (error) throw error;
      return ((data ?? []) as unknown as StudioContribution[]).map((r) => ({ ...r, spot: null }));
    },
    enabled: !!spotId,
    staleTime: 15_000,
  });
}

// ─── Readiness (Phase 4) ───────────────────────────────────────
// Thin facade over the `studio_spot_readiness` RPC. The wizard rail
// and the verification preflight derive their per-step state
// exclusively from this payload — no second engine in the frontend.

export interface StudioReadiness {
  found: boolean;
  spot_id: string | null;
  enrichment_level: string | null;
  enrichment_blockers: string[];
  missing_blocks: unknown;
  quality_status: import("@/lib/spotDisplayStatus").QualityStatus | null;
  content_status: {
    has_geo: boolean;
    has_description: boolean;
    has_entry: boolean;
    has_safety: boolean;
    has_recent_visibility: boolean;
    has_media: boolean;
    has_spot_point: boolean;
    has_default_variant: boolean;
  };
  task: {
    id: string;
    status: string | null;
    assignee_id: string | null;
    in_pilot: boolean | null;
  } | null;
  last_activity_at: string | null;
}

const EMPTY_CONTENT_STATUS: StudioReadiness["content_status"] = {
  has_geo: false,
  has_description: false,
  has_entry: false,
  has_safety: false,
  has_recent_visibility: false,
  has_media: false,
  has_spot_point: false,
  has_default_variant: false,
};

export function useStudioReadiness(spotId: string | undefined) {
  return useQuery({
    queryKey: ["spot-studio-readiness", spotId],
    queryFn: async (): Promise<StudioReadiness | null> => {
      if (!spotId) return null;
      const { data, error } = await supabase.rpc("studio_spot_readiness", {
        _spot_id: spotId,
      });
      if (error) throw error;
      const raw = (data ?? null) as Record<string, unknown> | null;
      if (!raw) return null;
      const cs = (raw.content_status ?? {}) as Partial<StudioReadiness["content_status"]>;
      const blockers = Array.isArray(raw.enrichment_blockers)
        ? (raw.enrichment_blockers as string[])
        : [];
      return {
        found: raw.found === true,
        spot_id: (raw.spot_id as string | null) ?? null,
        enrichment_level: (raw.enrichment_level as string | null) ?? null,
        enrichment_blockers: blockers,
        missing_blocks: raw.missing_blocks ?? null,
        quality_status: (raw.quality_status as StudioReadiness["quality_status"]) ?? null,
        content_status: { ...EMPTY_CONTENT_STATUS, ...cs },
        task: (raw.task as StudioReadiness["task"]) ?? null,
        last_activity_at: (raw.last_activity_at as string | null) ?? null,
      };
    },
    enabled: !!spotId,
    staleTime: 15_000,
  });
}